/**
 * Browser online/offline + pending-upload reporting.
 *
 * The Layout's NetworkBadge consumes the same online state via window events,
 * but recording-page logic also wants to (a) gate the BigRecordButton when
 * offline, (b) surface a banner if there's a pending upload backlog, and (c)
 * be explicit about which state changed (event source) for telemetry.
 *
 * Pending count comes from the @ohear/audio TakeStore — supplied externally
 * via {@link setPendingUploads} so this hook stays decoupled from IndexedDB.
 */
import { useEffect, useState } from 'react'

let pendingUploadsCount = 0
const subscribers = new Set<(count: number) => void>()

/**
 * Set the global pending-uploads count. Called by the recorder hook whenever
 * the UploadQueue progress callback fires.
 */
export function setPendingUploads(count: number): void {
  if (count === pendingUploadsCount) return
  pendingUploadsCount = count
  for (const sub of subscribers) sub(count)
}

export interface NetworkState {
  online: boolean
  /** Number of takes still queued / uploading client-side. */
  pendingUploads: number
  /** True if pendingUploads > 0 OR offline AND there's recorded but unsent audio. */
  hasUnsentRecordings: boolean
}

/**
 * Subscribe to online/offline events plus the cross-component pending-upload
 * counter. SSR-safe: returns `{online: true, pendingUploads: 0}` on the server.
 */
export function useNetwork(): NetworkState {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  const [pending, setPending] = useState<number>(pendingUploadsCount)

  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    const sub = (count: number) => setPending(count)
    subscribers.add(sub)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
      subscribers.delete(sub)
    }
  }, [])

  return {
    online,
    pendingUploads: pending,
    hasUnsentRecordings: pending > 0,
  }
}
